import { useEffect, useRef, useState } from "react";
import {
  Check, Eye, Send, FileCheck2, MessageCircle, Sparkles, Building2, Mail, Save, Plus, RefreshCw,
  ExternalLink, Landmark, Upload, ShieldCheck, Clock3, FileText, ClipboardList, PenLine, Lightbulb,
  Search, Bell, Play, History, Ban, Smartphone, ArrowRight, Download, Paperclip, TriangleAlert, Users,
} from "lucide-react";

type L = { pt: boolean };

/** Small "FillSign inside Dynamics" mark: mascot circle overlapping a D365 roundel. */
const WA_PATH = "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z";
export function WaIcon({ className = "" }: { className?: string }) {
  return <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true"><path d={WA_PATH} /></svg>;
}

export function BrandPair() {
  return (
    <div className="flex items-center" aria-hidden="true">
      <span className="z-10 flex h-14 w-14 items-center justify-center rounded-full border-4 border-[var(--fs-d365-blue)] bg-white shadow-md">
        <img src="/favicon.png" alt="" className="h-9 w-9" />
      </span>
      <span className="-ml-3 flex h-14 w-[4.5rem] items-center justify-end rounded-full bg-white/10 pr-4 text-sm font-semibold tracking-tight text-white ring-1 ring-white/25">
        D365
      </span>
    </div>
  );
}

/* ───────────────────────── Hero: AI-positioned signing ───────────────────────── */

function DocLines() {
  return (
    <div className="space-y-2.5" aria-hidden="true">
      <div className="h-2.5 w-2/5 rounded bg-gray-300" />
      <div className="h-2 w-full rounded bg-gray-100" />
      <div className="h-2 w-11/12 rounded bg-gray-100" />
      <div className="h-2 w-4/5 rounded bg-gray-100" />
      <div className="h-2 w-full rounded bg-gray-100" />
      <div className="h-2 w-3/5 rounded bg-gray-100" />
    </div>
  );
}

export function SignatureFlowMock({ pt }: L) {
  // Scene plays once, and only after the mock is visible: 0 detect → 1 Ana signs → 2 WhatsApp →
  // 3 Carlos signs → 4 sealed (stays). Starts 1.2s after entering the viewport, 1s between beats.
  const [scene, setScene] = useState(0);
  const [started, setStarted] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);
  const run = 0;
  useEffect(() => {
    const el = hostRef.current; if (!el || started) return;
    if (typeof IntersectionObserver === "undefined") { setStarted(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setStarted(true); io.disconnect(); } }, { threshold: 0.35 });
    io.observe(el); return () => io.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started || scene >= 4) return;
    const t = setTimeout(() => setScene((s) => s + 1), scene === 0 ? 1200 : 1000);
    return () => clearTimeout(t);
  }, [started, scene]);
  const anaSigned = scene >= 1, waSent = scene >= 2, carlosSigned = scene >= 3, sealed = scene === 4;
  const status = sealed ? (pt ? "2 de 2 assinaram" : "2 of 2 signed") : anaSigned ? (pt ? "1 de 2 assinou" : "1 of 2 signed") : (pt ? "Aguardando" : "Pending");
  const caption = [
    pt ? "IA encontrou 2 linhas de assinatura" : "AI found 2 signature lines",
    pt ? "Ana assinou pelo e-mail" : "Ana signed via email",
    pt ? "Carlos recebeu o link no WhatsApp" : "Carlos got the link on WhatsApp",
    pt ? "Carlos assinou pelo celular" : "Carlos signed on his phone",
    pt ? "Documento selado com hash e auditoria" : "Document sealed with hash and audit trail",
  ][scene];
  return (
    <div ref={hostRef} className="mx-auto w-full max-w-[560px]">
      <div data-depth="4" className="mb-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 text-[11px] text-gray-500">
        <span className="flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-2.5 py-1"><Building2 className="h-3 w-3 text-[var(--fs-d365-blue)]" />Dynamics 365</span>
        <span className="hidden h-px w-5 bg-gray-300 sm:block" />
        <span className="flex items-center gap-1.5 rounded-full bg-[var(--fs-forest)] px-2.5 py-1 font-medium text-white"><img src="/favicon.png" alt="" className="h-3.5 w-3.5" />FillSign</span>
        <span className="hidden h-px w-5 bg-gray-300 sm:block" />
        <span className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors ${waSent ? "border-[#1B7F3B]/40 bg-[#E7FFDB] text-[#1B7F3B]" : "border-black/10 bg-white"}`}><MessageCircle className="h-3 w-3" />WhatsApp</span>
        <span className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 transition-colors ${anaSigned ? "border-[var(--fs-sage)]/60 bg-[#F3F8EC] text-[var(--fs-forest)]" : "border-black/10 bg-white"}`}><Mail className="h-3 w-3" />E-mail</span>
      </div>

      <div className={`rounded-xl border bg-white shadow-[0_30px_70px_-40px_rgba(45,80,22,0.45)] transition-colors duration-500 ${sealed ? "border-[var(--fs-sage)]" : "border-black/10"}`}>
        <div className="flex items-center justify-between gap-3 border-b border-black/5 px-5 py-3">
          <div className="flex min-w-0 items-center gap-2 text-sm text-gray-700"><FileCheck2 className="h-4 w-4 shrink-0 text-[var(--fs-moss)]" /><span className="truncate">{pt ? "Contrato de prestação de serviços.pdf" : "Service agreement.pdf"}</span></div>
          <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${sealed ? "bg-[var(--fs-forest)] text-white" : "bg-[#F3F8EC] text-[var(--fs-forest)]"}`}>{status}</span>
        </div>

        <div className="px-4 pt-5 pb-4 sm:px-8 sm:pt-6 sm:pb-5">
          <DocLines />
          <div className="mt-5 flex h-4 items-center gap-2 text-[11px] text-[var(--fs-forest)]"><Sparkles className="h-3.5 w-3.5" />{caption}</div>

          <div className="mt-3 grid grid-cols-2 gap-3 sm:gap-6">
            {/* Ana */}
            <div className="relative rounded-md border border-dashed border-[var(--fs-sage)] p-2">
              <span className="absolute -top-2.5 left-2 rounded bg-[var(--fs-sage)] px-1.5 text-[10px] font-medium text-white">{pt ? "Assinatura 1" : "Signature 1"}</span>
              <svg viewBox="0 0 220 70" className="h-12 w-full" aria-hidden="true">
                {anaSigned && <path key={`a${run}`} className="fs-sign-stroke" d="M10 50 C 22 18, 34 14, 40 34 C 46 54, 54 40, 66 24 C 74 14, 78 30, 84 44 C 92 58, 100 40, 112 30 C 124 20, 136 44, 150 36 C 166 26, 178 14, 190 30 C 198 40, 206 34, 212 28" fill="none" stroke="#1B2A1E" strokeWidth="2.2" strokeLinecap="round" />}
              </svg>
              <div className="border-t border-gray-800 pt-1 text-[11px] text-gray-600">Ana Souza<span className="block text-[10px] text-gray-400">{anaSigned ? (pt ? "Assinado via e-mail · 09:41" : "Signed via email · 09:41") : (pt ? "Aguardando" : "Pending")}</span></div>
            </div>
            {/* Carlos */}
            <div className="relative rounded-md border border-dashed border-[var(--fs-sage)] p-2">
              <span className="absolute -top-2.5 left-2 rounded bg-[var(--fs-sage)] px-1.5 text-[10px] font-medium text-white">{pt ? "Assinatura 2" : "Signature 2"}</span>
              <div className="relative h-12">
                {!carlosSigned && <span className="absolute bottom-1 left-0 rounded bg-[#FFF6D6] px-2 py-0.5 text-[10px] font-medium text-[#8A6A00]">{pt ? "Assine aqui" : "Sign here"}</span>}
                <svg viewBox="0 0 220 70" className="h-12 w-full" aria-hidden="true">
                  {carlosSigned && <path key={`c${run}`} className="fs-sign-stroke" d="M12 44 C 30 10, 40 60, 52 30 C 60 12, 66 40, 78 42 C 96 44, 104 20, 118 22 C 132 24, 126 50, 146 46 C 164 42, 168 22, 184 26 C 198 30, 204 40, 212 34" fill="none" stroke="#1B2A1E" strokeWidth="2.2" strokeLinecap="round" />}
                </svg>
              </div>
              <div className={`pt-1 text-[11px] text-gray-600 ${carlosSigned ? "border-t border-gray-800" : "border-t border-dashed border-gray-400"}`}>Carlos Lima<span className="block text-[10px] text-gray-400">{carlosSigned ? (pt ? "Assinado via WhatsApp · 10:05" : "Signed via WhatsApp · 10:05") : waSent ? (pt ? "Link enviado no WhatsApp" : "Link sent on WhatsApp") : (pt ? "Aguardando" : "Pending")}</span></div>
            </div>
          </div>
        </div>

        <ul className="border-t border-black/5 px-4 py-2.5 text-[11px] text-gray-600 sm:px-5 sm:text-xs">
          <li className="flex items-center gap-2 py-1"><Send className="h-3.5 w-3.5 text-gray-400" />{pt ? "Enviado por e-mail e WhatsApp" : "Sent by email and WhatsApp"}<span className="ml-auto text-gray-400">09:12</span></li>
          <li className={`flex items-center gap-2 py-1 transition-opacity duration-500 ${anaSigned ? "opacity-100 text-[var(--fs-forest)]" : "opacity-30"}`}><Check className="h-3.5 w-3.5" />{pt ? "Ana assinou · IP, hora e hash registrados" : "Ana signed · IP, time and hash recorded"}<span className="ml-auto text-gray-400">09:41</span></li>
          <li className={`flex items-center gap-2 py-1 transition-opacity duration-500 ${carlosSigned ? "opacity-100 text-[var(--fs-forest)]" : "opacity-30"}`}><Check className="h-3.5 w-3.5" />{pt ? "Carlos assinou · autenticado por WhatsApp" : "Carlos signed · authenticated by WhatsApp"}<span className="ml-auto text-gray-400">10:05</span></li>
          <li className={`flex items-center gap-2 py-1 transition-opacity duration-500 ${sealed ? "opacity-100 font-medium text-[var(--fs-forest)]" : "opacity-30"}`}><ShieldCheck className="h-3.5 w-3.5" />{pt ? "Hash SHA-256 · documento selado" : "SHA-256 hash · document sealed"}<span className="ml-auto text-gray-400">10:05</span></li>
        </ul>
      </div>

      <div data-depth="7" className={`ml-auto mt-4 max-w-[280px] rounded-2xl rounded-tr-sm border border-black/5 bg-[#E7FFDB] px-4 py-3 text-xs text-gray-800 shadow-md transition-opacity duration-500 ${waSent ? "opacity-100" : "opacity-0"}`}>
        <div className="mb-1 flex items-center gap-1.5 font-medium text-[#1B7F3B]"><MessageCircle className="h-3.5 w-3.5" /> FillSign</div>
        {pt ? "Olá Carlos, o contrato está pronto para assinatura. Toque no link para assinar." : "Hi Carlos, the agreement is ready. Tap the link to sign."}
      </div>
    </div>
  );
}

/* ───────────── Dynamics 365: the real PCF, inside a standard Account form ───────────── */

export function FormFrame({ pt, children, bodyRef }: L & { children: React.ReactNode; bodyRef?: React.Ref<HTMLDivElement> }) {
  return (
    <div className="bg-[#F3F2F1] text-[#1B2A1E]">
      <div className="relative z-10 flex items-center gap-4 border-b border-black/10 bg-white px-4 py-2 text-[11px] text-gray-700">
        <span className="flex items-center gap-1"><Save className="h-3 w-3" />{pt ? "Salvar" : "Save"}</span>
        <span className="flex items-center gap-1"><Plus className="h-3 w-3" />{pt ? "Novo" : "New"}</span>
        <span className="flex items-center gap-1"><RefreshCw className="h-3 w-3" />{pt ? "Atualizar" : "Refresh"}</span>
        <span className="hidden sm:inline">{pt ? "Atribuir" : "Assign"}</span>
        <span className="hidden sm:inline">Flow</span>
        <span className="ml-auto text-gray-400">Dynamics 365</span>
      </div>
      <div className="relative z-10 border-b border-black/10 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#E1F0D7] text-[10px] font-semibold text-[var(--fs-forest)]">TF</span>
          <div>
            <div className="text-sm font-semibold leading-tight">Teste FillSign no Dynamics 365 CRM <span className="font-normal text-gray-400">- {pt ? "Salvo" : "Saved"}</span></div>
            <div className="text-[10px] text-gray-500">Account · Account</div>
          </div>
        </div>
        <div className="mt-2 flex gap-4 text-[11px] text-gray-500">
          <span className="border-b-2 border-[#1B2A1E] pb-1 font-medium text-[#1B2A1E]">{pt ? "Resumo" : "Summary"}</span>
          <span>{pt ? "Detalhes" : "Details"}</span>
          <span>{pt ? "Relacionado" : "Related"} ⌄</span>
        </div>
      </div>
      <div ref={bodyRef} className="grid gap-3 p-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
        <div className="hidden space-y-3 lg:block">
          <div className="rounded-md bg-white p-3">
            <div className="mb-2 text-[10px] font-semibold tracking-wide text-gray-600">{pt ? "ENDEREÇO" : "ADDRESS"}</div>
            {(pt ? ["Rua", "Cidade", "Estado", "CEP"] : ["Street", "City", "State", "ZIP"]).map((f) => (
              <div key={f} className="flex items-center gap-2 py-1 text-[10px]"><span className="w-14 text-gray-600">{f}</span><span className="h-4 flex-1 rounded bg-[#F3F2F1] px-2 leading-4 text-gray-400">---</span></div>
            ))}
          </div>
          <div className="rounded-md bg-white p-3 text-[10px] text-gray-500">{pt ? "Linha do tempo" : "Timeline"}<div className="mt-2 h-2 w-3/4 rounded bg-[#F3F2F1]" /><div className="mt-1.5 h-2 w-1/2 rounded bg-[#F3F2F1]" /></div>
        </div>
        <div className="rounded-md bg-white p-3">{children}</div>
      </div>
    </div>
  );
}

function PcfHeader({ pt }: L) {
  return (
    <>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-base font-semibold leading-none">Fill<span className="text-[var(--fs-sage)]">Sign</span></div>
          <div className="mt-0.5 text-[9px] tracking-wider text-gray-500">{pt ? "ASSINATURA DIGITAL" : "DIGITAL SIGNATURE"}</div>
        </div>
        <Lightbulb className="h-4 w-4 text-yellow-500" />
      </div>
      <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-2 py-0.5 text-[10px] text-[#1D4ED8]">
        <Sparkles className="h-3 w-3" />{pt ? "Posicionamento Inteligente ativado" : "Smart positioning enabled"}
      </span>
    </>
  );
}

export function PcfStep1({ pt, bare }: L & { bare?: boolean }) {
  const [opt, setOpt] = useState(1); // 1 = template (the configured default); hover previews the other option
  const tabs: Array<[string, string, string]> = pt
    ? [["Contatos (1)", "Contatos da entidade Contato do Dynamics", "rounded bg-[var(--fs-sage)] px-2 py-1 font-medium text-white"],
       ["Usuários (146)", "Usuários da entidade Usuário do Dynamics", "rounded border border-black/10 px-2 py-1"],
       ["+ Externo", "Adicione e envie o documento para pessoas de fora da organização", "rounded border border-black/10 px-2 py-1"]]
    : [["Contacts (1)", "People from the Dynamics Contact entity", "rounded bg-[var(--fs-sage)] px-2 py-1 font-medium text-white"],
       ["Users (146)", "People from the Dynamics User entity", "rounded border border-black/10 px-2 py-1"],
       ["+ External", "Add and send the document to people outside the organization", "rounded border border-black/10 px-2 py-1"]];
  return (
    <div className="text-[11px]">
      {!bare && <PcfHeader pt={pt} />}
      <div className="mt-3 rounded-md border border-black/10 p-3">
        <div className="flex items-center gap-1.5 font-semibold"><FileText className="h-3.5 w-3.5" />{pt ? "Documento" : "Document"}</div>
        <div className="mt-2 text-center text-gray-600">{pt ? "Como deseja enviar o documento?" : "How do you want to send the document?"}</div>
        <div className="relative mt-2 grid grid-cols-2 gap-2" onMouseLeave={() => setOpt(1)}>
          <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-[calc(50%-4px)] rounded-md border-2 border-[var(--fs-sage)] bg-[#F3F8EC] transition-transform duration-500 ease-[cubic-bezier(.2,.8,.2,1)]"
            style={{ transform: opt ? "translateX(calc(100% + 8px))" : "translateX(0)" }} />
          {[[FileText, pt ? "Upload de Documento" : "Upload a document", pt ? "PDF ou Word e adicione assinantes" : "PDF or Word, then add signers"],
            [ClipboardList, pt ? "Usar Template" : "Use a template", pt ? "Modelo com campos preenchidos automaticamente" : "Model with fields filled automatically"]].map(([Icon, title, sub], i) => {
            const I = Icon as typeof FileText;
            return (
              <div key={i} onMouseEnter={() => setOpt(i)}
                className={`relative rounded-md border-2 p-3 text-center transition-colors duration-500 ${opt === i ? "border-transparent" : "border-black/10"}`}>
                <I className={`mx-auto h-5 w-5 transition-colors duration-500 ${opt === i ? "text-[var(--fs-forest)]" : "text-gray-500"}`} />
                <div className="mt-1 font-medium">{title as string}</div>
                <div className={`mt-0.5 text-[10px] transition-colors duration-500 ${opt === i ? "text-gray-600" : "text-gray-500"}`}>{sub as string}</div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-2 rounded-md border border-black/10 p-3">
        <div className="flex items-center gap-1.5 font-semibold"><PenLine className="h-3.5 w-3.5" />{pt ? "Assinantes (1/3)" : "Signers (1/3)"}</div>
        <div className="mt-2 flex gap-1.5 text-[10px]">
          {tabs.map(([label, tip, cls]) => (
            <span key={label} className={`group relative cursor-default ${cls}`}>
              {label}
              <span role="tooltip" className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-44 -translate-x-1/2 translate-y-1 scale-95 rounded-md bg-[#1B2A1E] px-2.5 py-1.5 text-center text-[10px] font-normal leading-snug text-white opacity-0 shadow-lg transition-all duration-200 group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100">
                {tip}
                <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-[#1B2A1E]" />
              </span>
            </span>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2 rounded-md border border-black/10 px-2 py-1.5 text-[10px] text-gray-400"><Search className="h-3 w-3" />{pt ? "Buscar contato por nome ou email..." : "Search contact by name or email..."}</div>
        <div className="mt-2 flex items-center gap-2 rounded-md bg-[#F7F7F7] p-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#7CC4C4] text-[9px] font-semibold text-white">AS</span>
          <div className="flex-1">
            <div className="font-medium text-[#1D4ED8]">Ana Souza <span className="ml-1 rounded bg-[#E1F0D7] px-1 text-[9px] font-normal text-[var(--fs-forest)]">{pt ? "Contato" : "Contact"}</span></div>
            <div className="text-[10px] text-gray-500">ana@acme.com.br</div>
            <div className="mt-0.5 flex items-center gap-1 text-[10px] text-[#1B7F3B]"><Check className="h-3 w-3" /><span className="fs-wiggle"><WaIcon className="h-3 w-3 text-[#25D366]" /></span>{pt ? "Notificar via WhatsApp" : "Notify via WhatsApp"} <span className="rounded border border-[var(--fs-sage)] px-1 text-gray-700">(11) 9 1234-5678</span></div>
          </div>
          <span className="rounded-full border border-[#F9A8D4] bg-[#FDF2F8] px-1.5 text-[9px] text-[#9D174D]">AI</span>
        </div>
      </div>
      <div className="mt-2 text-right"><span className="inline-flex items-center gap-1 rounded-md bg-[var(--fs-sage)] px-3 py-1.5 font-medium text-white"><Sparkles className="h-3 w-3" />{pt ? "Revisar e Enviar para assinatura" : "Review and send for signature"}</span></div>
    </div>
  );
}

export function PcfStep2({ pt, bare }: L & { bare?: boolean }) {
  return (
    <div className="text-[11px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 font-semibold"><Sparkles className="h-3.5 w-3.5 text-[#DB2777]" />{pt ? "Posições sugeridas pela IA" : "Positions suggested by AI"}</div>
        <span className="text-gray-400">✕</span>
      </div>
      <div className="text-[10px] text-gray-500">{pt ? "1 assinante" : "1 signer"}</div>
      <div className="mt-2 flex items-center justify-center gap-2 text-[10px] text-gray-500"><span className="rounded border border-black/10 px-1.5">−</span>75%<span className="rounded border border-black/10 px-1.5">+</span></div>
      <div className="mt-2 flex justify-center rounded-md bg-[#EDEDED] p-4">
        <div className="w-40 rounded-sm bg-white p-3 shadow">
          <div className="text-center text-[7px] font-semibold">{pt ? "Declaração de Conta" : "Account statement"}</div>
          <div className="mt-1.5 space-y-1"><div className="h-1 rounded bg-gray-200" /><div className="h-1 rounded bg-gray-200" /><div className="h-1 w-3/4 rounded bg-gray-200" /></div>
          <div className="mt-2 border border-gray-300"><div className="grid grid-cols-2 border-b border-gray-300 text-[5px]"><span className="px-1">{pt ? "Empresa" : "Company"}</span><span className="px-1">Teste FillSign</span></div><div className="grid grid-cols-2 text-[5px]"><span className="px-1">Status</span><span className="px-1">{pt ? "Ativo" : "Active"}</span></div></div>
          <div className="mt-4">
            <div className="fs-ai-place mx-auto w-fit rounded border border-dashed border-[var(--fs-sage)] bg-[#F3F8EC] px-1.5 text-[6px] text-[var(--fs-forest)]">✎ Ana Souza</div>
            <div className="mt-px border-t border-gray-700" />
          </div>
        </div>
      </div>
      <div className="mt-2 text-[9px] italic text-gray-500">{pt ? "Se as posições não estiverem corretas, arraste os nomes para ajustar" : "If positions are off, drag the names to adjust"}</div>
      <div className="mt-2 flex flex-wrap justify-end gap-1.5"><span className="rounded-md border border-black/10 px-2 py-1">{pt ? "Cancelar" : "Cancel"}</span><span className="rounded-md border border-black/10 px-2 py-1">{pt ? "Ajustar manualmente" : "Adjust manually"}</span><span className="rounded-md bg-[var(--fs-sage)] px-2 py-1 font-medium text-white">✓ {pt ? "Confirmar posições" : "Confirm positions"}</span></div>
    </div>
  );
}

const SIGNERS: Array<[string, string, string, string]> = [
  ["Ana Souza", "ana@acme.com.br", "AS", "#7CC4C4"], ["Bruno Lima", "bruno@acme.com.br", "BL", "#A5B4FC"],
  ["Carla Mendes", "carla@acme.com.br", "CM", "#F5A97F"], ["Diego Rocha", "diego@acme.com.br", "DR", "#7FC99A"],
];

export function PcfStep3({ pt, bare }: L & { bare?: boolean }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1100);
    return () => window.clearInterval(id);
  }, []);
  const [name, mail, ini, color] = SIGNERS[Math.floor(tick / 4) % SIGNERS.length];
  const stage = tick % 4; // 0 sent · 1 viewed · 2 signed · 3 hold
  const dot = (on: boolean) => `h-1.5 w-1.5 rounded-full transition-colors duration-500 ${on ? "bg-[var(--fs-sage)]" : "bg-gray-300"}`;
  const bar = (on: boolean) => `h-px w-3 transition-colors duration-500 ${on ? "bg-[var(--fs-sage)]" : "bg-gray-300"}`;
  const signed = stage >= 2;
  return (
    <div className="text-[11px]">
      {!bare && <PcfHeader pt={pt} />}
      <div className="mt-3 rounded-md border border-black/10 p-3">
        <div className="flex items-center gap-1.5 font-semibold"><FileText className="h-3.5 w-3.5" />{pt ? "Documento" : "Document"}</div>
        <div className="mt-1.5 flex items-center gap-2 rounded-md bg-[#F7F7F7] p-2"><FileText className="h-4 w-4 text-gray-500" /><div><div className="font-medium">Contrato_ACME.pdf</div><div className="text-[10px] text-gray-500">{pt ? "Enviado em: 02/04/2026, 23:47" : "Sent on: 02 Apr 2026, 23:47"}</div></div></div>
      </div>
      <div className="mt-2 rounded-md border border-black/10 p-3">
        <div className="flex items-center gap-1.5 font-semibold"><PenLine className="h-3.5 w-3.5" />{pt ? "Assinantes (1/3)" : "Signers (1/3)"}</div>
        <div className="mt-1.5 flex items-center gap-2 rounded-md bg-[#F7F7F7] p-2">
          <span key={ini} className="fs-fade flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-semibold text-white" style={{ background: color }}>{ini}</span>
          <div key={name} className="fs-fade min-w-0 flex-1"><div className="font-medium text-[#1D4ED8]">{name}</div><div className="truncate text-[10px] text-gray-500">{mail}</div>
            <div className="mt-1 flex items-center gap-1 text-[8px] text-gray-500"><span className={dot(true)} />{pt ? "Enviado" : "Sent"}<span className={bar(stage >= 1)} /><span className={dot(stage >= 1)} />{pt ? "Visualizado" : "Viewed"}<span className={bar(signed)} /><span className={dot(signed)} />{pt ? "Assinado" : "Signed"}</div>
          </div>
          <Bell className="h-3.5 w-3.5 shrink-0 text-gray-600" /><span className="shrink-0 rounded border border-[#1B7F3B] p-0.5"><WaIcon className="h-3 w-3 text-[#25D366]" /></span>
        </div>
      </div>
      <div className={`mt-2 flex items-center justify-between rounded-md border-2 px-3 py-1.5 font-medium transition-colors duration-500 ${signed ? "border-[#BBE0B0] bg-[#EEF8EA] text-[var(--fs-forest)]" : "border-[#FDE68A] bg-[#FFFBEB]"}`}><span className="flex items-center gap-1">{signed ? <Check className="h-3 w-3" /> : <Clock3 className="h-3 w-3" />}Status: {signed ? (pt ? "Assinado" : "Signed") : (pt ? "Pendente" : "Pending")}</span><span className="text-[10px] font-normal">{signed ? (pt ? "1 de 1 assinaturas" : "1 of 1 signatures") : (pt ? "0 de 1 assinaturas" : "0 of 1 signatures")}</span></div>
      <div className="mt-2 flex flex-wrap justify-center gap-1.5 text-[10px]">
        <span className="rounded-md border border-black/10 px-2 py-1">{pt ? "Documento original" : "Original document"}</span>
        <span className="rounded-md bg-[#E1F0D7] px-2 py-1 text-gray-400">{pt ? "Documento assinado" : "Signed document"}</span>
        <span className="flex items-center gap-1 rounded-md border border-black/10 px-2 py-1"><History className="h-3 w-3" />{pt ? "Auditoria" : "Audit trail"}</span>
        <span className="flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-red-700"><Ban className="h-3 w-3" />{pt ? "Cancelar documento" : "Cancel document"}</span>
        <span className="flex items-center gap-1 rounded-md border border-black/10 px-2 py-1"><ExternalLink className="h-3 w-3" />{pt ? "Editar no FillSign" : "Edit on FillSign"}</span>
      </div>
      <div className="mt-2 rounded-md border border-[#BBE0B0] bg-[#EEF8EA] px-3 py-1.5 text-[10px] text-[var(--fs-forest)]">{pt ? "Documento enviado para assinatura!" : "Document sent for signature!"}</div>
    </div>
  );
}

/** Three real states of the PCF, switchable, on a fixed-height stage so the page never jumps. */
export function PcfShowcase({ pt }: L) {
  const [step, setStep] = useState(0);
  const labels = pt
    ? ["Documento e assinantes", "IA posiciona a assinatura", "Status, reenvio e auditoria"]
    : ["Document and signers", "AI places the signature", "Status, resend and audit trail"];
  const hint = pt
    ? ["Escolha upload ou template e quem assina: contatos e usuários do registro, ou externos.", "A IA sugere onde cada assinatura entra. Confirme ou arraste.", "Acompanhe cada assinante, reenvie por e-mail ou WhatsApp e baixe com auditoria."]
    : ["Pick upload or template and who signs: record contacts and users, or external.", "AI suggests where each signature goes. Confirm or drag.", "Track each signer, resend by email or WhatsApp and download with the audit trail."];
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {labels.map((l, i) => (
          <button key={l} type="button" onClick={() => setStep(i)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${step === i ? "bg-white text-[var(--fs-d365-blue)]" : "bg-white/10 text-white/80 hover:bg-white/20"}`}>
            <span className="mr-1.5 opacity-60">{i + 1}</span>{l}
          </button>
        ))}
        <span className="ml-auto hidden text-sm text-white/70 lg:block">{hint[step]}</span>
      </div>
      <div className="relative mt-5">
        <div className="pointer-events-none absolute -inset-x-10 -top-8 bottom-0 rounded-[2rem] bg-[radial-gradient(60%_60%_at_50%_0%,rgba(255,255,255,0.14),transparent_70%)]" aria-hidden="true" />
        <div className="relative overflow-hidden rounded-xl border border-white/15 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.6)]">
          <div className="flex items-center gap-2 bg-[#2B2B2B] px-3 py-2 text-[11px] text-gray-300"><span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" /><span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" /><span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" /><span className="ml-3 hidden truncate rounded bg-black/30 px-3 py-0.5 sm:block">org.crm2.dynamics.com/main.aspx?etn=account</span></div>
          <div className="h-[520px] overflow-y-auto overscroll-contain bg-[#F3F2F1] sm:h-[600px] sm:overflow-hidden">
            <div className={step === 0 ? "block" : "hidden"}><FormFrame pt={pt}><PcfStep1 pt={pt} /></FormFrame></div>
            <div className={step === 1 ? "block" : "hidden"}><FormFrame pt={pt}><PcfStep2 pt={pt} /></FormFrame></div>
            <div className={step === 2 ? "block" : "hidden"}><FormFrame pt={pt}><PcfStep3 pt={pt} /></FormFrame></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────── Inline YouTube: thumbnail first, iframe only after click ───────────── */
export function VideoFacade({ id, title }: { id: string; title: string }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="relative aspect-video overflow-hidden rounded-xl border border-white/15 bg-black shadow-2xl">
      {playing ? (
        <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      ) : (
        <button type="button" onClick={() => setPlaying(true)} className="group h-full w-full" aria-label={title}>
          <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100" loading="lazy" />
          <span className="absolute inset-0 flex items-center justify-center"><span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-[var(--fs-forest)] shadow-lg transition-transform group-hover:scale-105"><Play className="ml-1 h-7 w-7" /></span></span>
        </button>
      )}
    </div>
  );
}

/* ───────────── Templates: FillSign fields ← Dynamics record → ready contract ───────────── */

export function TemplateMappingMock({ pt }: L) {
  const ph = (t: string) => <span className="rounded bg-[#F3F8EC] px-1 font-mono text-[10px] text-[var(--fs-forest)]">{t}</span>;
  const rec: Array<[string, string]> = pt
    ? [["Nome", "ACME Distribuidora Ltda"], ["CNPJ", "12.345.678/0001-90"], ["E-mail", "ana@acme.com.br"], ["Valor da oportunidade", "R$ 48.000,00"]]
    : [["Name", "ACME Distribuidora Ltda"], ["Tax ID", "12.345.678/0001-90"], ["Email", "ana@acme.com.br"], ["Opportunity amount", "R$ 48,000.00"]];
  const Panel = ({ title, sub, children, dark = false }: { title: string; sub: string; children: React.ReactNode; dark?: boolean }) => (
    <div className={`flex flex-col rounded-xl border shadow-sm ${dark ? "border-[var(--fs-d365-blue)]/30 bg-[#F5F7FB]" : "border-black/10 bg-white"}`}>
      <div className="flex items-center justify-between border-b border-black/5 px-4 py-2.5 text-xs"><span className="font-medium">{title}</span><span className="text-gray-400">{sub}</span></div>
      <div className="flex-1 p-4">{children}</div>
    </div>
  );
  return (
    <div className="grid items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1fr]">
      <Panel title={pt ? "1 · Template no FillSign" : "1 · Template in FillSign"} sub="app.fillsign.com.br">
        <div className="space-y-2 text-[11px] leading-5 text-gray-700">
          <p className="font-semibold text-[#1B2A1E]">{pt ? "CONTRATO DE FORNECIMENTO" : "SUPPLY AGREEMENT"}</p>
          <p>{pt ? "Contratante: " : "Client: "}{ph(pt ? "{{conta.nome}}" : "{{account.name}}")}</p>
          <p>CNPJ: {ph(pt ? "{{conta.cnpj}}" : "{{account.taxid}}")}</p>
          <p>{pt ? "Valor: " : "Amount: "}{ph(pt ? "{{oportunidade.valor}}" : "{{opportunity.amount}}")}</p>
          <p>{pt ? "Contato: " : "Contact: "}{ph(pt ? "{{contato.email}}" : "{{contact.email}}")}</p>
          <div className="h-2 w-4/5 rounded bg-gray-100" />
          <p className="pt-1 text-[10px] text-gray-500">{pt ? "Campos mapeados uma vez, reutilizados em todo envio." : "Fields mapped once, reused on every send."}</p>
        </div>
      </Panel>
      <div className="hidden items-center lg:flex" aria-hidden="true"><ArrowRight className="h-6 w-6 text-[var(--fs-sage)]" /></div>
      <Panel title={pt ? "2 · Registro no Dynamics" : "2 · Dynamics record"} sub="Account · Opportunity" dark>
        <div className="space-y-1.5 text-[11px]">
          {rec.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2"><span className="w-24 shrink-0 text-gray-500">{k}</span><span className="flex-1 truncate rounded bg-white px-2 py-1 font-medium text-[#1B2A1E]">{v}</span></div>
          ))}
          <p className="pt-1 text-[10px] text-gray-500">{pt ? "Lido do registro aberto no momento do envio." : "Read from the open record at send time."}</p>
        </div>
      </Panel>
      <div className="hidden items-center lg:flex" aria-hidden="true"><ArrowRight className="h-6 w-6 text-[var(--fs-sage)]" /></div>
      <Panel title={pt ? "3 · Contrato pronto" : "3 · Contract ready"} sub="PDF">
        <div className="space-y-2 text-[11px] leading-5 text-gray-700">
          <p className="font-semibold text-[#1B2A1E]">{pt ? "CONTRATO DE FORNECIMENTO" : "SUPPLY AGREEMENT"}</p>
          <p>{pt ? "Contratante: " : "Client: "}<b className="font-medium text-[#1B2A1E]">ACME Distribuidora Ltda</b></p>
          <p>CNPJ: <b className="font-medium text-[#1B2A1E]">12.345.678/0001-90</b></p>
          <p>{pt ? "Valor: " : "Amount: "}<b className="font-medium text-[#1B2A1E]">{pt ? "R$ 48.000,00" : "R$ 48,000.00"}</b></p>
          <p>{pt ? "Contato: " : "Contact: "}<b className="font-medium text-[#1B2A1E]">ana@acme.com.br</b></p>
          <div className="mt-2 flex items-center gap-2 rounded-md bg-[#F3F8EC] px-2.5 py-1.5 text-[10px] text-[var(--fs-forest)]"><Check className="h-3 w-3" />{pt ? "IA posiciona a assinatura · pronto para enviar" : "AI places the signature · ready to send"}</div>
        </div>
      </Panel>
    </div>
  );
}

/* ───────────── GOV.BR: the whole signing round, run by FillSign (animated) ───────────── */

// Scene length of each step (ms). The loop starts when the block is on screen, pauses on hover
// and stops after a manual pick; prefers-reduced-motion keeps it still (CSS animations off too).
const GOVBR_MS = [3600, 4000, 4000, 3800, 5000, 6000];

type GovQ = "queue" | "sent" | "signing" | "returning" | "checking" | "ok";

export function GovBrFlowMock({ pt }: L) {
  const [step, setStep] = useState(0);
  const [auto, setAuto] = useState(true);
  const [visible, setVisible] = useState(false);
  const [hover, setHover] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [cycle, setCycle] = useState(0);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);
  useEffect(() => {
    const el = hostRef.current; if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.3 });
    io.observe(el); return () => io.disconnect();
  }, []);
  const running = auto && visible && !hover && !reduced;
  useEffect(() => { if (running) setCycle((c) => c + 1); }, [running]);
  useEffect(() => {
    if (!running) return;
    const t = setTimeout(() => setStep((s) => (s + 1) % GOVBR_MS.length), GOVBR_MS[step]);
    return () => clearTimeout(t);
  }, [running, step]);
  const pick = (i: number) => { setStep(i); setAuto(false); };
  const replay = () => { setStep(0); setAuto(true); };

  const steps: Array<[typeof Send, string, string]> = pt
    ? [[Send, "Você envia com GOV.BR", "Escolha o método GOV.BR e a ordem de quem assina. Daqui em diante, o FillSign conduz a rodada."],
       [Mail, "Quem é a vez recebe o guia", "E-mail com o PDF e o passo a passo para assinar no gov.br. Os outros esperam a vez deles."],
       [Landmark, "Assina no portal gov.br", "Com a conta gov.br, no assinador oficial: assinatura eletrônica avançada. Quem tem certificado ICP-Brasil pode usar o dele."],
       [Upload, "Devolve pelo mesmo link", "O PDF assinado volta pela página do FillSign, sem anexo perdido no e-mail."],
       [ShieldCheck, "O FillSign confere sozinho", "Assinatura válida, CPF certo, assinaturas anteriores intactas e nada alterado depois. Se algo não bate, você recebe o motivo."],
       [Users, "Segue a fila até o fim", "O próximo recebe o PDF já com as assinaturas anteriores. No fim, documento validado e auditoria completa."]]
    : [[Send, "You send with GOV.BR", "Pick the GOV.BR method and the signing order. From here on, FillSign runs the round."],
       [Mail, "Whoever's turn it is gets the guide", "Email with the PDF and a step-by-step guide to sign on gov.br. The others wait for their turn."],
       [Landmark, "Signs on the gov.br portal", "With their gov.br account, on the official signer: advanced electronic signature. Signers with an ICP-Brasil certificate can use theirs."],
       [Upload, "Returns it through the same link", "The signed PDF comes back through the FillSign page, no attachment lost in email."],
       [ShieldCheck, "FillSign checks it on its own", "Valid signature, right CPF, earlier signatures intact and nothing changed afterwards. If something is off, you get the reason."],
       [Users, "Follows the queue to the end", "The next signer gets the PDF with the earlier signatures in it. At the end, a validated document and a full audit trail."]];

  const people: Array<[string, string]> = [["Ana Souza", "AS"], ["Bruno Lima", "BL"], ["Carla Dias", "CD"]];
  const anaQ = (["sent", "sent", "signing", "returning", "checking", "ok"] as GovQ[])[step];
  const qLabel: Record<GovQ, string> = pt
    ? { queue: "Na fila", sent: "Guia enviado", signing: "Assinando no gov.br", returning: "Devolvendo o PDF", checking: "Conferindo", ok: "Conferido" }
    : { queue: "In line", sent: "Guide sent", signing: "Signing on gov.br", returning: "Returning the PDF", checking: "Checking", ok: "Checked" };
  const qTone: Record<GovQ, string> = {
    queue: "bg-gray-100 text-gray-500", sent: "bg-[#E8F0FE] text-[#1351B4]", signing: "bg-[#E8F0FE] text-[#1351B4]",
    returning: "bg-[#FFF6D6] text-[#8A6A00]", checking: "bg-[#FFF6D6] text-[#8A6A00]", ok: "bg-[#F3F8EC] text-[var(--fs-forest)]",
  };
  const guide = pt
    ? ["Abra o assinador gov.br", "Assine com sua conta gov.br", "Devolva o PDF pelo link"]
    : ["Open the gov.br signer", "Sign with your gov.br account", "Return the PDF through the link"];
  const checks = pt
    ? ["Assinatura válida no PDF", "CPF do certificado confere", "Assinaturas anteriores intactas", "Nada alterado depois de assinar", "Certificado de raiz confiável"]
    : ["Valid signature in the PDF", "Certificate CPF matches the signer", "Earlier signatures intact", "Nothing changed after signing", "Certificate from a trusted root"];
  const waitText = pt
    ? ["", "Ana recebeu o guia por e-mail", "Ana está assinando no portal gov.br", "Recebendo o PDF assinado da Ana"]
    : ["", "Ana got the guide by email", "Ana is signing on the gov.br portal", "Receiving Ana's signed PDF"];
  const d = (ms: number) => ({ animationDelay: `${ms}ms` });
  const phoneOn = step >= 1 && step <= 3;
  const Avatar = ({ ini }: { ini: string }) => (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#E1F0D7] text-[9px] font-semibold text-[var(--fs-forest)]">{ini}</span>
  );
  const label = (on: boolean) => `mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${on ? "text-[var(--fs-moss)]" : "text-gray-400"}`;
  const caption = "text-[10px] font-medium uppercase tracking-wide text-gray-400";

  let phone: React.ReactNode;
  if (step === 0) {
    phone = (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <div className="text-3xl font-light tracking-tight text-[#1B2A1E]">09:12</div>
        <div className="mt-1 text-[10px] text-gray-400">{pt ? "Celular da Ana" : "Ana's phone"}</div>
        <div className="mt-6 rounded-lg border border-dashed border-black/10 px-3 py-2 text-[10px] text-gray-400">{pt ? "Aguardando o envio" : "Waiting for the send"}</div>
      </div>
    );
  } else if (step === 1) {
    phone = (
      <div className="space-y-2">
        <div className="fs-govbr-drop rounded-xl border border-black/5 bg-white p-2 shadow-md">
          <div className="flex items-center gap-1.5 text-[9px] text-gray-500"><img src="/favicon.png" alt="" className="h-3 w-3" />FillSign · {pt ? "agora" : "now"}</div>
          <div className="mt-0.5 text-[10px] font-semibold leading-tight">{pt ? "Sua vez: assine com gov.br" : "Your turn: sign with gov.br"}</div>
        </div>
        <div className="fs-fade rounded-lg border border-black/10 p-2.5 text-[10px] leading-snug text-gray-700" style={d(600)}>
          <div className="font-semibold text-[#1B2A1E]">{pt ? "Olá, Ana" : "Hi Ana"}</div>
          <p className="mt-0.5">{pt ? "O contrato está pronto para você assinar no gov.br." : "The contract is ready for you to sign on gov.br."}</p>
          <div className="mt-2 flex items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1.5 text-[9px]"><Paperclip className="h-3 w-3 text-gray-400" />Contrato - Alfa Ltda.pdf</div>
          <ol className="mt-2 space-y-1">
            {guide.map((g, i) => (
              <li key={g} className="fs-fade flex items-center gap-1.5" style={d(1000 + i * 350)}>
                <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-[#1351B4] text-[8px] font-semibold text-white">{i + 1}</span>{g}
              </li>
            ))}
          </ol>
          <div className="fs-fade mt-2 rounded-md bg-[#1351B4] px-2 py-1.5 text-center text-[10px] font-medium text-white" style={d(2200)}>{pt ? "Abrir passo a passo" : "Open the guide"}</div>
        </div>
      </div>
    );
  } else if (step === 2) {
    phone = (
      <div className="flex h-full flex-col">
        <div className="-mx-3 -mt-3 flex items-center justify-between rounded-t-[1.1rem] bg-[#1351B4] px-3 py-2 text-white">
          <span className="text-[12px] font-bold tracking-tight">gov.br</span><span className="text-[9px] opacity-80">{pt ? "Assinador" : "Signer"}</span>
        </div>
        <div className="mt-3 text-[10px] font-medium text-gray-700">Contrato - Alfa Ltda.pdf</div>
        <div className="mt-2 space-y-1.5" aria-hidden="true">
          <div className="h-1.5 w-full rounded bg-gray-100" /><div className="h-1.5 w-11/12 rounded bg-gray-100" /><div className="h-1.5 w-4/5 rounded bg-gray-100" /><div className="h-1.5 w-full rounded bg-gray-100" />
        </div>
        <div className="mt-3 rounded-md border border-[#1351B4]/30 bg-[#F5F8FF] p-2">
          <svg viewBox="0 0 220 60" className="h-9 w-full" aria-hidden="true">
            <path className="fs-sign-stroke" style={d(500)} d="M10 44 C 22 14, 34 12, 40 30 C 46 50, 56 36, 66 22 C 74 12, 80 28, 86 40 C 94 54, 104 36, 116 26 C 128 16, 140 40, 154 32 C 170 22, 182 12, 194 28 C 202 38, 208 32, 212 26" fill="none" stroke="#1351B4" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
          <div className="border-t border-[#1351B4]/30 pt-1 text-[9px] leading-tight text-[#1351B4]"><b className="font-semibold">ANA SOUZA</b><br />{pt ? "Assinado com gov.br" : "Signed with gov.br"} · 09:31</div>
        </div>
        <span className="fs-govbr-pop mt-2 self-start rounded-full bg-[#E8F0FE] px-2 py-0.5 text-[9px] font-medium text-[#1351B4]" style={d(1500)}>{pt ? "Assinatura eletrônica avançada" : "Advanced electronic signature"}</span>
        <div className="fs-fade mt-auto flex items-center justify-center gap-1 rounded-md border border-[#1351B4] px-2 py-1.5 text-[10px] font-medium text-[#1351B4]" style={d(2200)}><Download className="h-3 w-3" />{pt ? "Baixar PDF assinado" : "Download signed PDF"}</div>
      </div>
    );
  } else if (step === 3) {
    phone = (
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-1.5 text-[10px] font-medium"><img src="/favicon.png" alt="" className="h-3.5 w-3.5" />FillSign</div>
        <div className="mt-1 text-[11px] font-semibold">{pt ? "Devolver PDF assinado" : "Return the signed PDF"}</div>
        <div className="mt-0.5 text-[9px] text-gray-500">Contrato - Alfa Ltda</div>
        <div className="mt-3 rounded-lg border-2 border-dashed border-[#1351B4]/40 bg-[#F5F8FF] p-3 text-center">
          <Upload className="mx-auto h-5 w-5 text-[#1351B4]" />
          <div className="fs-fade mt-2 flex items-center justify-center gap-1 text-[9px] text-gray-700" style={d(250)}><FileText className="h-3 w-3 shrink-0" />{pt ? "Contrato (assinado).pdf" : "Contract (signed).pdf"}</div>
          <div className="mt-2 h-1.5 overflow-hidden rounded bg-white"><div className="fs-govbr-fill h-full w-full rounded bg-[#1351B4]" /></div>
        </div>
        <div className="fs-govbr-pop mt-3 flex items-center justify-center gap-1.5 rounded-md bg-[#F3F8EC] px-2 py-1.5 text-[10px] font-medium text-[var(--fs-forest)]" style={d(2300)}><Check className="h-3.5 w-3.5" />{pt ? "Arquivo enviado" : "File sent"}</div>
        <p className="mt-auto text-center text-[9px] text-gray-400">{pt ? "Mesmo link do e-mail. Nada de anexo de volta." : "Same link as the email. No reply attachment."}</p>
      </div>
    );
  } else if (step === 4) {
    phone = (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <Clock3 className="h-6 w-6 text-[#1351B4] motion-safe:animate-pulse" />
        <div className="mt-2 text-[11px] font-semibold">{pt ? "Recebido" : "Received"}</div>
        <div className="mt-0.5 text-[10px] text-gray-500">{pt ? "Conferindo as assinaturas…" : "Checking the signatures…"}</div>
      </div>
    );
  } else {
    phone = (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <span className="fs-govbr-pop flex h-9 w-9 items-center justify-center rounded-full bg-[var(--fs-moss)] text-white"><Check className="h-5 w-5" /></span>
        <div className="mt-2 text-[11px] font-semibold">{pt ? "Tudo certo, Ana" : "All set, Ana"}</div>
        <div className="mt-0.5 text-[10px] text-gray-500">{pt ? "Sua assinatura foi conferida." : "Your signature was checked."}</div>
      </div>
    );
  }

  let panel: React.ReactNode;
  if (step === 0) {
    panel = (
      <div className="space-y-3 p-4 text-xs">
        <div>
          <div className={caption}>{pt ? "Método de assinatura" : "Signing method"}</div>
          <div className="mt-1.5 grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1 text-[11px]">
            <span className="rounded-md px-2 py-1 text-center text-gray-500">{pt ? "Eletrônica FillSign" : "FillSign e-signature"}</span>
            <span className="fs-govbr-pop rounded-md bg-white px-2 py-1 text-center font-medium text-[#1351B4] shadow-sm" style={d(300)}>GOV.BR</span>
          </div>
        </div>
        <div>
          <div className={caption}>{pt ? "Ordem de assinatura" : "Signing order"}</div>
          <ol className="mt-1.5 space-y-1.5">
            {people.map(([n], i) => (
              <li key={n} className="fs-fade flex items-center gap-2 rounded-md border border-black/10 px-2 py-1.5" style={d(600 + i * 250)}>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F3F8EC] text-[10px] font-semibold text-[var(--fs-forest)]">{i + 1}</span>
                <span className="flex-1 truncate">{n}</span>
                <span className="text-[10px] text-gray-400">{i === 0 ? (pt ? "primeiro" : "first") : (pt ? "depois" : "next")}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="fs-govbr-pop flex items-center gap-2 rounded-md border border-[#BBE0B0] bg-[#EEF8EA] px-3 py-2 text-[11px] text-[var(--fs-forest)]" style={d(1900)}>
          <Send className="h-3.5 w-3.5 shrink-0" />{pt ? "Enviado para Ana. Bruno e Carla recebem na vez deles." : "Sent to Ana. Bruno and Carla get it on their turn."}
        </div>
      </div>
    );
  } else if (step <= 3) {
    panel = (
      <div className="p-4 text-xs">
        <div className={caption}>{pt ? "Fila de assinatura" : "Signing queue"}</div>
        <ol className="mt-2 space-y-1.5">
          {people.map(([n, ini], i) => {
            const q: GovQ = i === 0 ? anaQ : "queue";
            return (
              <li key={n} className={`flex items-center gap-2 rounded-md border px-2 py-2 ${i === 0 ? "border-[#1351B4]/30 bg-[#F5F8FF]" : "border-black/10"}`}>
                <Avatar ini={ini} /><span className="flex-1 truncate">{n}</span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${qTone[q]}`}>{qLabel[q]}</span>
              </li>
            );
          })}
        </ol>
        <div className="mt-3 flex items-center gap-2 text-[11px] text-gray-500"><Clock3 className="h-3.5 w-3.5 shrink-0 text-[#1351B4] motion-safe:animate-pulse" />{waitText[step]}</div>
      </div>
    );
  } else if (step === 4) {
    panel = (
      <div className="p-4 text-xs">
        <div className={caption}>{pt ? "Conferência automática · Ana" : "Automatic check · Ana"}</div>
        <ul className="mt-2 space-y-1.5">
          {checks.map((c, i) => (
            <li key={c} className="flex items-center gap-2">
              <span className="fs-govbr-pop flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[var(--fs-moss)] text-white" style={d(300 + i * 450)}><Check className="h-3 w-3" /></span>
              <span className="fs-fade" style={d(250 + i * 450)}>{c}</span>
            </li>
          ))}
        </ul>
        <div className="fs-govbr-pop mt-3 flex items-center gap-2 rounded-md bg-[var(--fs-forest)] px-3 py-2 text-[11px] font-medium text-white" style={d(2700)}><ShieldCheck className="h-4 w-4 shrink-0" />{pt ? "Aprovado automaticamente" : "Approved automatically"}</div>
        <p className="fs-fade mt-2 flex items-start gap-1.5 text-[10px] leading-snug text-gray-500" style={d(3300)}>
          <TriangleAlert className="mt-px h-3 w-3 shrink-0 text-[#8A6A00]" />{pt ? "Se algo não confere, você recebe “Conferir assinatura gov.br” com o motivo e decide." : "If something is off, you get a review request with the reason and decide."}
        </p>
      </div>
    );
  } else {
    panel = (
      <div className="p-4 text-xs">
        <div className={caption}>{pt ? "Fila de assinatura" : "Signing queue"}</div>
        <ol className="mt-2 space-y-1.5">
          {people.map(([n, ini], i) => (
            <li key={n} className="rounded-md border border-black/10 px-2 py-1.5">
              <div className="flex items-center gap-2">
                <Avatar ini={ini} /><span className="flex-1 truncate">{n}</span>
                <span className="fs-govbr-pop flex shrink-0 items-center gap-0.5 rounded-full bg-[#F3F8EC] px-2 py-0.5 text-[10px] font-medium text-[var(--fs-forest)]" style={d(i === 0 ? 0 : i === 1 ? 1500 : 3000)}><Check className="h-3 w-3" />{qLabel.ok}</span>
              </div>
              {i > 0 && (
                <div className="fs-fade mt-1 pl-7 text-[10px] text-gray-500" style={d(i === 1 ? 300 : 1800)}>
                  {pt ? `recebeu o PDF com ${i === 1 ? "a assinatura da Ana" : "as assinaturas de Ana e Bruno"}` : `got the PDF with ${i === 1 ? "Ana's signature" : "Ana's and Bruno's signatures"}`}
                </div>
              )}
            </li>
          ))}
        </ol>
        <div className="fs-govbr-pop mt-3 flex items-center justify-between gap-2 rounded-md border border-[#BBE0B0] bg-[#EEF8EA] px-3 py-2 text-[11px]" style={d(3900)}>
          <span className="flex items-center gap-1.5 font-medium text-[var(--fs-forest)]"><FileCheck2 className="h-3.5 w-3.5 shrink-0" />{pt ? "Concluído · 3 de 3" : "Done · 3 of 3"}</span>
          <span className="rounded-md bg-[var(--fs-moss)] px-2 py-1 text-[10px] font-medium text-white">{pt ? "Baixar com auditoria" : "Download with audit trail"}</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={hostRef} className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start"
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div>
        <ol className="space-y-1.5">
          {steps.map(([Icon, title, desc], i) => {
            const on = i === step;
            return (
              <li key={title}>
                <button type="button" onClick={() => pick(i)} aria-current={on ? "step" : undefined}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${on ? "border-[var(--fs-moss)] bg-white shadow-sm" : "border-transparent hover:bg-white/60"}`}>
                  <div className="flex items-center gap-3">
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors ${on ? "bg-[var(--fs-moss)] text-white" : i < step ? "bg-[#E1F0D7] text-[var(--fs-forest)]" : "bg-[#F3F8EC] text-[var(--fs-forest)]"}`}>
                      {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                    </span>
                    <Icon className={`h-4 w-4 shrink-0 ${on ? "text-[var(--fs-moss)]" : "text-gray-400"}`} />
                    <span className="font-semibold">{title}</span>
                  </div>
                  {on && <p className="fs-fade mt-1.5 pl-10 text-sm leading-relaxed text-gray-600">{desc}</p>}
                  {on && running && (
                    <span className="ml-10 mt-2 block h-0.5 w-24 overflow-hidden rounded bg-[#E1F0D7]">
                      <span key={`${step}-${cycle}`} className="fs-govbr-progress block h-full bg-[var(--fs-moss)]" style={{ animationDuration: `${GOVBR_MS[step]}ms` }} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
        {!auto && !reduced && (
          <button type="button" onClick={replay} className="ml-3 mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--fs-moss)] hover:underline">
            <Play className="h-3.5 w-3.5" />{pt ? "Ver a animação de novo" : "Play the animation again"}
          </button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-[214px_1fr]" aria-hidden="true">
        <div className={`${phoneOn ? "" : "hidden sm:block"} transition-opacity duration-500 ${phoneOn ? "opacity-100" : "sm:opacity-50"}`}>
          <div className={`${label(phoneOn)} justify-center`}><Smartphone className="h-3 w-3" />{pt ? "O que o signatário vê" : "What the signer sees"}</div>
          <div className="mx-auto h-[360px] w-[214px] overflow-hidden rounded-[1.6rem] border-[6px] border-[#1B2A1E] bg-white p-3 shadow-xl">
            <div key={`ph${step}`} className="fs-fade h-full">{phone}</div>
          </div>
        </div>
        <div className={`${phoneOn ? "hidden sm:block" : ""} min-w-0 transition-opacity duration-500 ${phoneOn ? "sm:opacity-50" : "opacity-100"}`}>
          <div className={label(!phoneOn)}><img src="/favicon.png" alt="" className="h-3 w-3" />{pt ? "O que você acompanha" : "What you track"}</div>
          <div className="h-[360px] overflow-hidden rounded-xl border border-black/10 bg-white shadow-[0_30px_70px_-40px_rgba(45,80,22,0.45)]">
            <div className="flex items-center justify-between gap-2 border-b border-black/5 px-4 py-2.5 text-xs">
              <span className="flex min-w-0 items-center gap-2 font-medium"><FileText className="h-4 w-4 shrink-0 text-[var(--fs-moss)]" /><span className="truncate">Contrato - Alfa Ltda.pdf</span></span>
              <span className="shrink-0 rounded-full bg-[#E8F0FE] px-2 py-0.5 text-[10px] font-medium text-[#1351B4]">GOV.BR</span>
            </div>
            <div key={`pn${step}`} className="fs-fade">{panel}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────── How it works: three steps, each with its own live panel ───────────── */

export function StepsInteractive({ pt, active: controlled }: L & { active?: number }) {
  const [inner, setInner] = useState(0);
  const [auto, setAuto] = useState(controlled === undefined);
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => setInner((a) => (a + 1) % 3), 4200);
    return () => clearInterval(id);
  }, [auto]);
  const active = controlled ?? inner;
  const pick = (i: number) => { setInner(i); setAuto(false); };
  const steps = pt
    ? [[Upload, "Envie o documento", "PDF ou Word. A IA encontra as linhas de assinatura; você confirma ou arrasta."],
       [Send, "Escolha o canal", "E-mail, WhatsApp ou os dois. O signatário recebe um link exclusivo e assina no celular, sem conta."],
       [FileCheck2, "Receba assinado", "Documento final com IP, data e hora, autenticação e hash. Válido pela MP 2.200-2 e Lei 14.063."]]
    : [[Upload, "Upload the document", "PDF or Word. AI finds the signature lines; you confirm or drag."],
       [Send, "Pick the channel", "Email, WhatsApp or both. The signer gets a unique link and signs on their phone, no account."],
       [FileCheck2, "Get it back signed", "Final document with IP, timestamp, authentication and hash. Valid under Brazilian e-signature law."]];
  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-center">
      <ol className="space-y-2">
        {steps.map(([Icon, title, desc], i) => {
          const I = Icon as typeof Upload; const on = i === active;
          return (
            <li key={title as string}>
              <button type="button" onClick={() => pick(i)} onMouseEnter={() => pick(i)}
                className={`w-full rounded-lg border p-4 text-left transition-colors ${on ? "border-[var(--fs-moss)] bg-white shadow-sm" : "border-transparent hover:bg-white/60"}`}>
                <div className="flex items-center gap-3">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${on ? "bg-[var(--fs-moss)] text-white" : "bg-[#F3F8EC] text-[var(--fs-forest)]"}`}>{i + 1}</span>
                  <I className={`h-5 w-5 ${on ? "text-[var(--fs-moss)]" : "text-gray-400"}`} />
                  <span className="text-lg font-semibold">{title as string}</span>
                </div>
                <p className={`mt-2 pl-11 leading-relaxed text-gray-600 ${on ? "" : "hidden lg:block"}`}>{desc as string}</p>
                {on && auto && controlled === undefined && <span className="mt-3 ml-11 block h-0.5 w-24 overflow-hidden rounded bg-[#E1F0D7]"><span className="fs-step-progress block h-full bg-[var(--fs-moss)]" /></span>}
              </button>
            </li>
          );
        })}
      </ol>
      <div className="rounded-xl border border-black/10 bg-white p-5 shadow-[0_30px_70px_-40px_rgba(45,80,22,0.45)]">
        {active === 0 && (
          <div className="text-xs">
            <div className="rounded-lg border-2 border-dashed border-[var(--fs-sage)] bg-[#FAFCF7] p-6 text-center">
              <Upload className="mx-auto h-6 w-6 text-[var(--fs-moss)]" />
              <div className="mt-2 font-medium">Contrato_ACME.docx</div>
              <div className="text-[11px] text-gray-500">{pt ? "Word convertido para PDF · 2 páginas" : "Word converted to PDF · 2 pages"}</div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-[var(--fs-forest)]"><Sparkles className="h-4 w-4" />{pt ? "IA encontrou 2 linhas de assinatura" : "AI found 2 signature lines"}</div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {["Ana Souza", "Carlos Lima"].map((n, k) => (
                <div key={n} className="relative rounded-md border border-dashed border-[var(--fs-sage)] p-2 pt-4"><span className="absolute -top-2 left-2 rounded bg-[var(--fs-sage)] px-1.5 text-[10px] text-white">{pt ? `Assinatura ${k + 1}` : `Signature ${k + 1}`}</span><div className="h-6 border-b border-gray-700" /><div className="mt-1 text-[11px] text-gray-600">{n}</div></div>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2"><span className="rounded-md border border-black/10 px-3 py-1.5">{pt ? "Ajustar" : "Adjust"}</span><span className="rounded-md bg-[var(--fs-moss)] px-3 py-1.5 font-medium text-white">{pt ? "Confirmar posições" : "Confirm positions"}</span></div>
          </div>
        )}
        {active === 1 && (
          <div className="text-xs">
            <div className="font-medium">{pt ? "Como cada pessoa recebe" : "How each person receives it"}</div>
            {[["Ana Souza", "ana@acme.com.br", "(11) 9 1234-5678", true], ["Carlos Lima", "carlos@acme.com.br", "", false]].map(([n, e, ph, wa]) => (
              <div key={n as string} className="mt-2 flex items-center gap-3 rounded-md border border-black/10 p-2.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#E1F0D7] text-[10px] font-semibold text-[var(--fs-forest)]">{(n as string).split(" ").map((x) => x[0]).join("")}</span>
                <div className="min-w-0 flex-1"><div className="font-medium">{n as string}</div><div className="truncate text-[11px] text-gray-500">{e as string}</div></div>
                <span className="flex items-center gap-1 rounded-full bg-[#F3F8EC] px-2 py-0.5 text-[10px] text-[var(--fs-forest)]"><Mail className="h-3 w-3" />E-mail</span>
                <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${wa ? "bg-[#E7FFDB] text-[#1B7F3B]" : "bg-gray-100 text-gray-400"}`}><MessageCircle className="h-3 w-3" />{wa ? (ph as string) : "WhatsApp"}</span>
              </div>
            ))}
            <div className="mt-4 ml-auto max-w-[260px] rounded-2xl rounded-tr-sm bg-[#E7FFDB] px-3 py-2 text-[11px] text-gray-800"><span className="font-medium text-[#1B7F3B]">FillSign</span> · {pt ? "Olá Ana, o contrato está pronto para assinatura. Toque no link para assinar." : "Hi Ana, the agreement is ready to sign. Tap the link."}</div>
            <div className="mt-3 text-[11px] text-gray-500">{pt ? "Sem conta, sem app: abre no navegador do celular." : "No account, no app: opens in the phone browser."}</div>
          </div>
        )}
        {active === 2 && (
          <div className="text-xs">
            <div className="flex items-center justify-between rounded-md border border-[#BBE0B0] bg-[#EEF8EA] px-3 py-2"><span className="flex items-center gap-2 font-medium text-[var(--fs-forest)]"><Check className="h-4 w-4" />Contrato_ACME.pdf · {pt ? "assinado por 2 de 2" : "signed by 2 of 2"}</span><span className="rounded-md bg-[var(--fs-moss)] px-2.5 py-1 font-medium text-white">{pt ? "Baixar com auditoria" : "Download with audit trail"}</span></div>
            <ul className="mt-3 divide-y divide-black/5 rounded-md border border-black/10">
              {(pt
                ? [["Enviado por e-mail e WhatsApp", "09:12"], ["Ana visualizou · IP 177.34.x.x", "09:38"], ["Ana assinou · autenticada por e-mail", "09:41"], ["Carlos assinou · autenticado por WhatsApp", "10:05"], ["Hash SHA-256 gerado · documento selado", "10:05"]]
                : [["Sent by email and WhatsApp", "09:12"], ["Ana viewed · IP 177.34.x.x", "09:38"], ["Ana signed · authenticated by email", "09:41"], ["Carlos signed · authenticated by WhatsApp", "10:05"], ["SHA-256 hash generated · document sealed", "10:05"]]
              ).map(([t, h]) => (
                <li key={t} className="flex items-center justify-between px-3 py-2"><span className="flex items-center gap-2"><Clock3 className="h-3 w-3 text-gray-400" />{t}</span><span className="text-gray-400">{h}</span></li>
              ))}
            </ul>
            <div className="mt-3 text-[11px] text-gray-500">{pt ? "Trilha de auditoria anexada ao PDF final. MP 2.200-2 · Lei 14.063." : "Audit trail attached to the final PDF. Brazilian e-signature law."}</div>
          </div>
        )}
      </div>
    </div>
  );
}
